import React from 'react';
import Icon from '@ant-design/icons';

/**
 * Bookmark ribbon — the save-for-later glyph.
 *
 * antd ships no bookmark icon (`@ant-design/icons-svg` has Book/BookFilled/
 * BookTwoTone and nothing else in that family), and Book is a different
 * metaphor: a closed book cover reads as "reading list" or "documentation".
 * The convention for saving a job to come back to is the ribbon — the flag with
 * the notched bottom edge — which is what LinkedIn and Indeed use for this exact
 * action, and what X, Reddit, Medium and Pocket use for save.
 *
 * viewBox is antd's own `64 64 896 896`, not `0 0 1024 1024`: antd's icons are
 * drawn in a 1024 grid but presented cropped to that inner 896 box, so matching
 * it is what makes this glyph the same optical size as its neighbours rather
 * than 12% smaller.
 *
 * One centreline path serves both variants: the outline strokes it, the filled
 * one strokes AND fills it. That guarantees the two states have an identical
 * silhouette and advance width — the saved and unsaved rows cannot shift by a
 * pixel, which matters because this sits in a 63px column next to the star
 * scale. Stroke width 72 is antd's own outline weight (BookOutlined's border is
 * 160→232 = 72), so it sits at the same optical weight as sibling icons.
 * The path is inset 36 (half the stroke) from the extremes so the stroke stays
 * inside the viewBox.
 */
const BOOKMARK_PATH =
  'M348 148h328a48 48 0 0148 48v680L512 720 300 876V196a48 48 0 0148-48z';

const svg = (filled: boolean) =>
  function BookmarkSvg(props: React.SVGProps<SVGSVGElement>) {
    return (
      <svg viewBox="64 64 896 896" width="1em" height="1em" {...props}>
        <path
          d={BOOKMARK_PATH}
          fill={filled ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={72}
          strokeLinejoin="round"
        />
      </svg>
    );
  };

const OutlineSvg = svg(false);
const FilledSvg = svg(true);

type Props = Omit<React.ComponentProps<typeof Icon>, 'component' | 'ref'>;

/** Unsaved state. */
export const BookmarkOutlined: React.FC<Props> = (props) => (
  <Icon component={OutlineSvg} {...props} />
);

/** Saved state. */
export const BookmarkFilled: React.FC<Props> = (props) => (
  <Icon component={FilledSvg} {...props} />
);
